import * as React from 'react'

import { useQuery } from '@apollo/client'
import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import {
  EmployeeSelectorV2ItemsDocument,
  EmployeeSelectorV2ItemsQuery,
  EmployeeSelectorV2ItemsQueryVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import TextField from 'modules/core/components/TextField'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import DailyBreakdown from 'modules/expenses/components/expenses/New/PerDiemWizard/components/DailyBreakdown'
import { usePerDiemWizard } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import {
  formatDateRange,
  generateDefaultTripName,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/generateDefaultTripName'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

import AdditionalInformation from './_AdditionalInformation'
import BudgetAndProject from './_BudgetAndProject'
import CostCenters from './_CostCenters'

const ReviewAndSubmit = () => {
  const { data, updateData, calculation } = usePerDiemWizard()
  const {
    access: { locale },
    employee,
  } = useCurrentSession()

  const currency = calculation.currency || 'EUR'
  const employeeId = employee?.id

  // Format start date for budget options query
  const effectiveOnDate = data.startDate ? data.startDate.toISOString().split('T')[0] : undefined

  // Fetch budget options
  const { budgetOptions, loading: loadingBudgets } = useBudgetOptions({
    employeeId,
    effectiveOn: effectiveOnDate,
    skip: !employeeId,
  })

  // Fetch project workers
  const { projectWorkers, loading: loadingProjects } = useProjectsAssignedToEmployees({
    employeeIds: employeeId ? [employeeId] : [],
    skip: !employeeId,
  })

  // Transform budget options to match component interface
  const budgetSelectOptions = budgetOptions.map((budget) => ({
    label: budget.label,
    value: budget.value.toString(),
  }))

  // Transform project workers to project options
  const projectSelectOptions = projectWorkers.map((pw) => ({
    label: pw.imputableProject?.name ?? '',
    value: pw.id.toString(),
  }))

  // Get subprojects for selected project
  const selectedProjectWorker = projectWorkers.find((pw) => pw.id === data.projectId)
  const subprojectSelectOptions =
    selectedProjectWorker?.imputableProject?.subprojects?.map((sp) => ({
      label: sp.name,
      value: sp.id?.toString() ?? '',
    })) ?? []

  const defaultTripName = React.useMemo(
    () => generateDefaultTripName(data.destinations, data.startDate, data.endDate),
    [data.destinations, data.startDate, data.endDate]
  )

  const tripNameValue = data.tripName || defaultTripName

  // Fetch requester employee data
  const { data: requesterData } = useQuery<
    EmployeeSelectorV2ItemsQuery,
    EmployeeSelectorV2ItemsQueryVariables
  >(EmployeeSelectorV2ItemsDocument, {
    variables: { ids: data.employeeId ? [data.employeeId] : [] },
    skip: !data.employeeId,
  })

  const requesterEmployee = requesterData?.employees?.employeeNamesConnection?.nodes?.[0]

  // Calculate trip duration
  const tripDuration = React.useMemo(() => {
    if (!data.startDate || !data.endDate) {
      return null
    }
    const start = moment(data.startDate)
    const end = moment(data.endDate)
    const days = end.diff(start, 'days') + 1 // +1 to include both start and end days
    const nights = days - 1
    return { days, nights }
  }, [data.startDate, data.endDate])

  // Count company-paid deductions
  const companyPaidDeductionsCount = React.useMemo(() => {
    return data.dayExclusions.reduce((count, exclusion) => {
      const exclusionCount =
        (exclusion.breakfastProvided ? 1 : 0) +
        (exclusion.lunchProvided ? 1 : 0) +
        (exclusion.dinnerProvided ? 1 : 0) +
        (exclusion.accommodationProvided ? 1 : 0)
      return count + exclusionCount
    }, 0)
  }, [data.dayExclusions])

  // Format destination names with "roundtrip" if applicable
  const destinationText = React.useMemo(() => {
    const destinationNames = data.destinations
      .filter((dest) => dest.locationName)
      .map((dest) => dest.locationName)
      .join(' - ')

    if (data.destinations.length > 1) {
      return `${destinationNames}${i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.roundtrip_suffix')}`
    }
    return destinationNames
  }, [data.destinations])

  // Format date range with duration
  const dateRangeText = React.useMemo(() => {
    if (!data.startDate || !data.endDate || !tripDuration) {
      return null
    }
    const dateRange = formatDateRange(data.startDate, data.endDate)
    const daysLabel = i18n.tp('expenses_v2.forms.germany_per_diem_modal.trip_details.days', {
      count: tripDuration.days,
    })
    const nightsLabel = i18n.tp('expenses_v2.forms.germany_per_diem_modal.trip_details.nights', {
      count: tripDuration.nights,
    })
    const durationStr = i18n.t(
      'expenses_v2.forms.germany_per_diem_modal.steps.step3.date_range_duration',
      {
        days: tripDuration.days,
        daysLabel,
        nights: tripDuration.nights,
        nightsLabel,
      }
    )
    return `${dateRange} ${durationStr}`
  }, [data.startDate, data.endDate, tripDuration])

  return (
    <Box flexDirection="column" gap="s8">
      <Text.Normal size="300" weight="semibold" color="grey1000">
        {i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.title')}
      </Text.Normal>

      <Text.Normal size="200" weight="medium" color="grey700"></Text.Normal>

      <Box flexDirection="column" gap="s4" paddingBottom="s16">
        <TextField
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_label')}
          placeholder={i18n.t(
            'expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_placeholder'
          )}
          value={tripNameValue}
          onChange={(value?: string | null) => updateData({ tripName: value || undefined })}
        />
        {!data.tripName && defaultTripName && (
          <Text.Normal size="200" weight="normal" color="grey700">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_suggestion')}
          </Text.Normal>
        )}
      </Box>

      {requesterEmployee && (
        <Box flexDirection="column" gap="s4" paddingBottom="s8">
          <Text.Normal size="200" weight="normal" color="grey700">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.owner')}
          </Text.Normal>
          <Box flexDirection="row" alignItems="center" gap="s4">
            <UserAvatar
              size="s40"
              user={{
                avatar: requesterEmployee.access?.avatar || null,
                fullName: requesterEmployee.fullName || '',
              }}
            />
            <Text.Normal size="200" weight="medium" color="grey1000">
              {requesterEmployee.fullName}
            </Text.Normal>
          </Box>
        </Box>
      )}
      <Divider vertical={false} />

      <Box flexDirection="column" gap="s4" paddingY="s16">
        <Text.Normal size="200" weight="normal" color="grey700">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.details')}
        </Text.Normal>

        <Box paddingLeft="s8">
          <ul>
            {destinationText && (
              <li style={{ display: 'list-item', listStyle: 'inside' }}>
                <Box inline>
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {destinationText}
                  </Text.Normal>
                </Box>
              </li>
            )}
            {dateRangeText && (
              <li style={{ display: 'list-item', listStyle: 'inside' }}>
                <Box inline>
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {dateRangeText}
                  </Text.Normal>
                </Box>
              </li>
            )}
            {companyPaidDeductionsCount > 0 && (
              <li style={{ display: 'list-item', listStyle: 'inside' }}>
                <Box inline>
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {i18n.tp(
                      'expenses_v2.forms.germany_per_diem_modal.steps.step3.company_paid_deductions',
                      {
                        count: companyPaidDeductionsCount,
                      }
                    )}
                  </Text.Normal>
                </Box>
              </li>
            )}
          </ul>
        </Box>
      </Box>

      <Divider vertical={false} />

      <BudgetAndProject
        budgetOptions={budgetSelectOptions}
        projectOptions={projectSelectOptions}
        subprojectOptions={subprojectSelectOptions}
        loadingBudgets={loadingBudgets}
        loadingProjects={loadingProjects}
      />

      {employeeId && <CostCenters employeeId={employeeId} />}

      <AdditionalInformation />

      <DailyBreakdown
        days={calculation.days ?? null}
        totalAmountCents={calculation.totalAmountCents}
        currency={currency}
        locale={locale}
      />
    </Box>
  )
}

export default ReviewAndSubmit
